import React from 'react';
import { useEffect, useState } from 'react';
import NumberInput from '~ComponentsImproved/Numpad';
import { useStorage } from '~Components/Storage';
import { useI18n } from '~Hooks';
import useConfirm from '~Hooks/useConfirm';
import { v4 as uuid } from 'uuid';
import { MethodProps } from '../types';

const CustomWithTip: React.FunctionComponent<MethodProps & {
  fail?: boolean;
}> = ({ payment, amount, orderAmount, onComplete, onFail, fail = false }) => {
  const [storage] = useStorage();
  const { i18n } = useI18n();
  const { confirm } = useConfirm();
  const [showTipModal, setShowTipModal] = useState(!payment.disableTip && storage.enableTipping);

  useEffect(() => {
    if (showTipModal) {
      return;
    }

    const handlePayment = () => {
      if (fail) {
        onFail(i18n('Swish.PaymentFailed'));
      } else {
        onComplete(undefined, { transactionId: uuid() });
      }
    };

    setTimeout(handlePayment, 2500);
  }, []);

  if (showTipModal) {
    return (
      <NumberInput
        key="tip"
        initialValue={'0'}
        decimal
        title={i18n('TM.Tip')}
        onSubmit={async (tipAmount) => {
          // Ensure input is valid
          const num = parseFloat(tipAmount);
          if (isNaN(num)) {
            onFail(i18n('Payment.Canceled'));
            return;
          }

          const tip = num * 100;
          const paymentAmount = amount ?? payment.amount ?? orderAmount;

          // Guard against the payment amount being typed into the tip prompt.
          // Production incident: after a card-terminal failure, staff retried
          // as Swish and re-entered the payment amount when the tip numpad
          // appeared — creating a phantom 100% tip that flowed into reports
          // and bookkeeping. An unusually large tip requires confirmation.
          if (paymentAmount > 0 && tip >= paymentAmount * 0.5) {
            const confirmed = await confirm(
              i18n('Payment.ConfirmLargeTip', num, paymentAmount / 100),
            );

            if (!confirmed) {
              // Keep the tip numpad open so staff can correct the value
              return;
            }
          }

          setShowTipModal(false);

          // Complete payment with added tip after a slight delay
          setTimeout(() => onComplete(undefined, { tip, transactionId: uuid() }), 2000);
        }}
        onClose={() => {
          onFail(i18n('Payment.Canceled'));
        }}
      />
    );
  } else {
    // Complete payment without tip
    setTimeout(() => onComplete(undefined, { transactionId: uuid() }), 2000);
  }

  return null;
};

export default CustomWithTip;
